import { useContext } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';

import {
	ANALYTICS_ACTION,
	ANALYTICS_SOURCE,
	ANALYTICS_ORIGIN,
	ANALYTICS_SUBJECT,
	ANALYTICS_SUBJECT_ID,
} from '../constants/snippetAnalytics.ts';
import { DeleteSnippetModal } from '../modals/DeleteSnippetModal.tsx';
import { DuplicateSnippetModal } from '../modals/DuplicateSnippetModal.tsx';
import { EditSnippetModal } from '../modals/EditSnippetModal.tsx';
import { SnippetScope, type Snippet } from '../snippetTypes.ts';

export type SnippetActionHandlersOptions = {
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

export const useSnippetActionHandlers = ({
	contentId,
	spaceId,
	spaceKey,
}: SnippetActionHandlersOptions) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showModal } = useDialogs();
	const { editorAPI } = useContext(EditorContextPanelContext);

	const firePanelAction = (
		actionSubjectId: string,
		snippetId: string,
		scope: SnippetScope = SnippetScope.USER,
	) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: ANALYTICS_SOURCE,
				actionSubject: ANALYTICS_SUBJECT.BUTTON,
				action: ANALYTICS_ACTION.CLICKED,
				actionSubjectId,
				type: 'ui',
				attributes: {
					origin: ANALYTICS_ORIGIN.SNIPPET_PANEL,
					snippetId,
					scope,
				},
			},
		}).fire();
	};

	const handleEdit = (snippet: Snippet) => {
		firePanelAction(ANALYTICS_SUBJECT_ID.EDIT_SNIPPET, snippet.snippetId);
		showModal(EditSnippetModal, {
			snippet,
			editorAPI,
			contentId,
			spaceId,
			spaceKey,
		});
	};

	const handleDuplicate = (snippet: Snippet) => {
		firePanelAction(ANALYTICS_SUBJECT_ID.DUPLICATE_SNIPPET, snippet.snippetId, snippet.scope);
		showModal(DuplicateSnippetModal, {
			snippet,
			editorAPI,
			contentId,
			spaceId,
			spaceKey,
		});
	};

	const handleDelete = (snippet: Snippet) => {
		firePanelAction(ANALYTICS_SUBJECT_ID.DELETE_SNIPPET, snippet.snippetId);
		showModal(DeleteSnippetModal, { snippet, editorAPI });
	};

	return { handleEdit, handleDuplicate, handleDelete };
};
